import { useAnstellungsverhaeltnisSchema } from '@/domain/mitarbeiter/features/anlageassistent/hooks/schema/useAnstellungsverhaeltnisSchema';
import { usePersonSchema } from '@/domain/mitarbeiter/features/anlageassistent/hooks/schema/usePersonSchema';
import { SubStepKey } from '@/domain/mitarbeiter/features/anlageassistent/types/types';
import z from 'zod';
import { useBezahlungSchema } from '@/domain/mitarbeiter/features/anlageassistent/hooks/schema/useBezahlungSchema';
import { useAbwesenheitenSchema } from '@/domain/mitarbeiter/features/anlageassistent/hooks/schema/useAbwesenheitenSchema';
import { useTranslation } from 'react-i18next';

export function useFormSchema() {
  const { t } = useTranslation();
  const { PersoenlichesSchema, KontaktSchema, ArbeitsbereicheSchema } =
    usePersonSchema();
  const { anstellungSchema, FristenSchema } = useAnstellungsverhaeltnisSchema();
  const { BezahlungsartSchema, ArbeitszeitkontoSchema, ZuschlaegeSchema } =
    useBezahlungSchema();
  const {
    AbwesenheitenAllgemeinSchema,
    FeiertagseinstellungenSchema,
    GuttageSchema,
  } = useAbwesenheitenSchema();

  return z
    .object({
      person: z.object({
        persoenliches: PersoenlichesSchema,
        kontakt: KontaktSchema,
        arbeitsbereiche: ArbeitsbereicheSchema,
      }),
      anstellungsverhaeltnis: z.object({
        fristen: z.array(FristenSchema),
        anstellung: anstellungSchema,
      }),
      bezahlung: z.object({
        bezahlungsart: BezahlungsartSchema,
        arbeitszeitkonto: ArbeitszeitkontoSchema,
        zuschlaege: ZuschlaegeSchema,
      }),
      abwesenheiten: z.object({
        allgemein: AbwesenheitenAllgemeinSchema,
        guttage: GuttageSchema,
        feiertagseinstellungen: FeiertagseinstellungenSchema,
      }),
      speicheroptionen: z.object({
        personalzugang: z.boolean(),
        personalfragebogen: z.boolean(),
        personalfragebogenNachricht: z.string().nullable(),
      }),
    })
    .superRefine((data, ctx) => {
      if (data.speicheroptionen.personalzugang && !data.person.kontakt.email) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'domain.mitarbeiter.create.steps.person.kontakt.form.email.validationErrorRequired',
          ),
          path: ['person', 'kontakt', 'email'],
        });
      }
    });
}

export function useSubStepSchemasMap() {
  const formSchema = useFormSchema();

  return {
    'person.persoenliches': formSchema.shape.person.shape.persoenliches,
    'person.kontakt': formSchema.shape.person.shape.kontakt,
    'person.arbeitsbereiche': formSchema.shape.person.shape.arbeitsbereiche,
    'anstellungsverhaeltnis.anstellung':
      formSchema.shape.anstellungsverhaeltnis.shape.anstellung,
    'anstellungsverhaeltnis.fristen':
      formSchema.shape.anstellungsverhaeltnis.shape.fristen,
    'bezahlung.bezahlungsart': formSchema.shape.bezahlung.shape.bezahlungsart,
    'bezahlung.arbeitszeitkonto':
      formSchema.shape.bezahlung.shape.arbeitszeitkonto,
    'bezahlung.zuschlaege': formSchema.shape.bezahlung.shape.zuschlaege,
    'abwesenheiten.allgemein': formSchema.shape.abwesenheiten.shape.allgemein,
    'abwesenheiten.feiertagseinstellungen':
      formSchema.shape.abwesenheiten.shape.feiertagseinstellungen,
    'abwesenheiten.guttage': formSchema.shape.abwesenheiten.shape.guttage,
    'speicheroptionen.personalzugang':
      formSchema.shape.speicheroptionen.shape.personalzugang,
    'speicheroptionen.personalfragebogen':
      formSchema.shape.speicheroptionen.shape.personalfragebogen,
    'speicheroptionen.personalfragebogenNachricht':
      formSchema.shape.speicheroptionen.shape.personalfragebogenNachricht,
  } satisfies Record<SubStepKey, z.ZodTypeAny>;
}
